import { Divider, Grid, Typography } from '@mui/material';
import { AccountRecord, Transaction } from '@tassat/fred-definitions';
import { AssetTypes } from '@tassat/ui-definitions';
import { formatAsAsset } from '@tassat/utility';
import moment from 'moment';
import * as React from 'react';

import muiStyles from './styles';
import { ILastTwoAssetTransactionsProps } from './types';
import { validateWalletAddress } from '../helpers/helpers';

const determineTxStatus = (tx: Transaction, selectedWallet: AccountRecord) => {
    const type = String.fromCharCode(tx.TransType);
    if (type === 'R') {
        return 'Redeem';
    }

    if (type === 'D') {
        return 'Deposit';
    }

    if (tx.SenderAddress !== selectedWallet.Account) {
        return 'Received';
    }

    return 'Sent';
};

const determineHeaderText = (type) => {
    switch (type) {
        case 'S':
            return 'Last two sends with this recipient:';
        case 'D':
        case 'R':
            return `Last two ${type === 'D' ? 'deposits' : 'redeems'} with this account:`;
        default:
            return 'Last two transactions:';
    }
};

export default function LastTwoAssetTransactionsWidget(props: ILastTwoAssetTransactionsProps) {
    const {
        type,
        recipientAddress,
        getTransactionsSync,
        selectedWallet,
        assets
    } = props;

    const [ transactions, setTransactions ] = React.useState<Transaction[]>();
    const classes = muiStyles(props);

    const fetchTransactions = async () => {
        let condition = '';
        if (type === 'S') {
            const rcptAddressError = validateWalletAddress(recipientAddress);
            if (rcptAddressError || !recipientAddress) {
                setTransactions([]);

                return;
            }

            // will search S by default, which is only internal sends
            const tranTypeCondition = `tranType|EQ|${type}`;
            const statusCondition = 'Status|EQ|C';

            const rcptAddress = recipientAddress.trim() !== '' && `RecipientAddress|EQ|${recipientAddress.trim()}`;
            condition = selectedWallet && selectedWallet.FirmId && selectedWallet.Account && rcptAddress ?
                `firmId|EQ|${selectedWallet.FirmId}:wallet|EQ|${selectedWallet.Account}:${tranTypeCondition}:${rcptAddress}:${statusCondition}`
                : '';
        } else {
            condition = selectedWallet && selectedWallet.FirmId && recipientAddress && type ?
                `firmId|EQ|${selectedWallet.FirmId}:wallet|EQ|${recipientAddress}:tranType|EQ|${type}:Status|IN|C,F`
                : '';
        }

        const res = await getTransactionsSync({
            Condition: condition,
            Sort: 'auditTime|DESC',
            PageNo: 1,
            RecordPerPage: 2
        });
        setTransactions(res.data.transactions.slice(0, 2));
    };

    React.useEffect(() => {
        fetchTransactions();
    }, [ type, recipientAddress ]);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid item>
                <Typography
                    className={classes.headerText}
                    variant='body1'
                >
                    {determineHeaderText(type)}
                </Typography>
            </Grid>
            {transactions &&
                transactions.map((transaction) => {
                const date = moment.unix(Number(transaction.AuditTime) / 1000);
                if (!date.isValid()) return null;

                const status = determineTxStatus(transaction, selectedWallet);
                const amountClass = status === 'Deposit' || status === 'Received' ?
                    classes.greenText
                    : classes.redText;
                const transactionAsset = assets[transaction.AssetId];

                return (
                    <React.Fragment key={transaction.RequestId}>
                        <Divider />
                        <Grid
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Grid
                                alignItems='center'
                                container
                                direction='column'
                                item
                                justifyContent='center'
                                spacing={0}
                                xs={1}
                            >
                                <Typography variant='body1'>
                                    {date.format('MMM')
                                        .toUpperCase()}
                                </Typography>
                                <Typography variant='h6'>
                                    {date.format('DD')}
                                </Typography>
                            </Grid>
                            <Grid
                                className={classes.transactionDescriptionContainer}
                                container
                                direction='column'
                                item
                                spacing={0}
                                xs={7}
                            >
                                <Typography variant='h6'>{status}</Typography>
                                <Typography variant='body1'>
                                    {
                                        transaction.PublicDescription &&
                                        `Public - ${transaction.PublicDescription}`
                                    }
                                </Typography>
                                <Typography variant='body1'>
                                    {
                                        transaction.PrivateDescription &&
                                        `Private - ${transaction.PrivateDescription}`
                                    }
                                </Typography>
                            </Grid>
                            <Grid
                                container
                                direction='column'
                                item
                                spacing={0}
                                xs={4}
                            >
                                <Typography
                                    className={amountClass}
                                    variant='h6'
                                >
                                    {transactionAsset.Type === AssetTypes.CURRENCY ?
                                        formatAsAsset(transaction.AssetQuantity, transactionAsset, 'curcode')
                                        : formatAsAsset(transaction.AssetQuantity, transactionAsset, 'ticker', undefined, true)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                );
            })}
            <Divider />
        </Grid>
    );
}